const normalizeText = value => String(value || '').trim();
const normalizeKey = value =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const normalizeInboxId = value => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? String(num) : '';
};

const countSimulationMessages = result => {
  const plan = Array.isArray(result?.send_plan) ? result.send_plan : [];
  if (plan.length) return plan.map(item => item?.content).filter(Boolean).length;

  const responses = result?.response?.responses;
  if (Array.isArray(responses) && responses.length) {
    return responses.map(item => item?.content).filter(Boolean).length;
  }

  return result?.response?.response ? 1 : 0;
};

const resolveDeliveryMode = result => {
  const trace = result?.debug_trace || result?.response_debug_trace || {};
  const refs = trace?.deploy_refs || {};
  return normalizeText(
    refs.effective_mode || result?.response?.delivery_mode || result?.delivery_mode
  );
};

export const parseBatchSimulationInput = ({
  input = '',
  defaultInboxId = '',
} = {}) =>
  String(input || '')
    .split(/\r?\n/)
    .map((line, index) => {
      const trimmed = normalizeText(line);
      if (!trimmed || trimmed.startsWith('#')) return null;

      const match = trimmed.match(/^(\d+)\s*(?:\||,|\t|::)\s*(.+)$/);
      const inboxId = normalizeText(match?.[1] || defaultInboxId);
      const query = normalizeText(match?.[2] || trimmed);
      if (!query) return null;

      return {
        id: `batch-case-${index + 1}`,
        lineNumber: index + 1,
        inboxId,
        query,
        label: query.length > 96 ? `${query.slice(0, 96)}...` : query,
      };
    })
    .filter(Boolean);

export const normalizeSavedBatchTestSet = value => {
  const row = value && typeof value === 'object' ? value : {};
  const title = normalizeText(row.title || row.name || row.label);
  const setKey = normalizeText(row.set_key || row.setKey) || normalizeKey(title);
  const defaultInboxId = normalizeInboxId(
    row.default_inbox_id || row.defaultInboxId
  );
  const cases = Array(row.cases)
    .map((item, index) => {
      const entry = item && typeof item === 'object' ? item : {};
      const query = normalizeText(entry.query);
      if (!query) return null;

      return {
        id: `saved-batch-case-${index + 1}`,
        query,
        inboxId: normalizeInboxId(entry.inbox_id || entry.inboxId),
      };
    })
    .filter(Boolean)
    .slice(0, 25);

  if (!title || !setKey || !cases.length) return null;

  return {
    id: row.id || null,
    set_key: setKey,
    title,
    default_inbox_id: defaultInboxId,
    case_count: cases.length,
    updated_at: normalizeText(row.updated_at || row.updatedAt),
    cases,
  };
};

export const normalizeSavedBatchTestSets = sets =>
  Array(sets)
    .map(normalizeSavedBatchTestSet)
    .filter(Boolean)
    .filter(
      (set, index, rows) =>
        rows.findIndex(item => item.set_key === set.set_key) === index
    )
    .slice(0, 12);

export const mergeSavedBatchTestSets = ({
  sets = [],
  set = null,
  limit = 12,
} = {}) => {
  const normalizedSet = normalizeSavedBatchTestSet(set);
  const normalizedRows = normalizeSavedBatchTestSets(sets);
  if (!normalizedSet) return normalizedRows.slice(0, Number(limit) || 12);

  return [
    normalizedSet,
    ...normalizedRows.filter(row => row.set_key !== normalizedSet.set_key),
  ].slice(0, Number(limit) || 12);
};

export const findSavedBatchTestSet = ({ sets = [], setKey = '' } = {}) => {
  const key = normalizeText(setKey);
  if (!key) return null;

  return (
    normalizeSavedBatchTestSets(sets).find(set => set.set_key === key) || null
  );
};

export const formatSavedBatchTestSetInput = set => {
  const normalizedSet = normalizeSavedBatchTestSet(set);
  if (!normalizedSet) return '';

  return normalizedSet.cases
    .map(item =>
      item.inboxId ? `${item.inboxId} | ${item.query}` : item.query
    )
    .join('\n');
};

export const buildSavedBatchTestSetLibraryExport = ({
  sets = [],
  source = 'ui',
  exportedAt = new Date().toISOString(),
} = {}) => ({
  schema_version: 1,
  source,
  exported_at: normalizeText(exportedAt),
  saved_test_sets: normalizeSavedBatchTestSets(sets).map(set => ({
    set_key: set.set_key,
    title: set.title,
    default_inbox_id: set.default_inbox_id || null,
    case_count: set.case_count,
    updated_at: set.updated_at || '',
    cases: Array(set.cases).map(item => ({
      inbox_id: item.inboxId || null,
      query: item.query,
    })),
  })),
});

export const normalizeSavedBatchTestSetLibraryImport = payload => {
  const row = payload && typeof payload === 'object' ? payload : {};
  return normalizeSavedBatchTestSets(
    row.saved_test_sets || row.savedTestSets || row.sets || payload
  );
};

export const summarizeBatchSimulationResult = ({
  result = null,
  runtimeStatus = 'inactive',
  connectedInboxCount = 0,
} = {}) => {
  const deliveryMode = resolveDeliveryMode(result);
  const messageCount = countSimulationMessages(result);
  const trace = result?.debug_trace || result?.response_debug_trace || {};
  const contentGapReason = normalizeText(trace?.content_gap_reason);
  const scopeWarning = normalizeText(trace?.article_scope_warning);

  const blockers = [];
  const watchSignals = [];

  if (runtimeStatus !== 'active') blockers.push('Runtime inactive');
  if (connectedInboxCount <= 0) blockers.push('No connected inbox');
  if (messageCount <= 0) blockers.push('No output generated');
  if (contentGapReason) blockers.push(`Content gap ${contentGapReason}`);
  if (scopeWarning) blockers.push(`Scope warning ${scopeWarning}`);

  if (!blockers.length && ['handoff', 'auto_suggest', 'suggest'].includes(deliveryMode)) {
    watchSignals.push(`Delivery ${deliveryMode.replace(/_/g, ' ')}`);
  }

  const level = blockers.length ? 'blocked' : watchSignals.length ? 'watch' : 'ready';

  return {
    level,
    deliveryMode,
    messageCount,
    contentGapReason,
    scopeWarning,
    blockers,
    watchSignals,
  };
};

export const summarizeBatchSimulationRows = rows => {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const counts = normalizedRows.reduce(
    (acc, row) => {
      const level = normalizeText(row?.level);
      if (level === 'ready') acc.ready += 1;
      else if (level === 'watch') acc.watch += 1;
      else acc.blocked += 1;

      const deliveryMode = normalizeText(row?.deliveryMode || 'unknown');
      acc.deliveryModeCounts[deliveryMode] =
        (acc.deliveryModeCounts[deliveryMode] || 0) + 1;
      return acc;
    },
    { ready: 0, watch: 0, blocked: 0, deliveryModeCounts: {} }
  );

  const total = normalizedRows.length;
  const overallLevel =
    counts.blocked > 0 ? 'blocked' : counts.watch > 0 ? 'watch' : total > 0 ? 'ready' : 'idle';

  return {
    total,
    ready: counts.ready,
    watch: counts.watch,
    blocked: counts.blocked,
    deliveryModeCounts: counts.deliveryModeCounts,
    overallLevel,
  };
};
